import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiPhone, FiMapPin, FiShield } = FiIcons;

const Footer = () => {
  const navigate = useNavigate();

  const scrollToConsultation = () => {
    const element = document.getElementById('consultation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToPage = (path) => {
    navigate(path);
    // Scroll to top after navigation
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  return (
    <footer className="gradient-navy text-white">
      {/* CTA Section */}
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Protect Your Digital Brand?
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Join hundreds of corporations who trust Namvoro with their most valuable digital assets.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToConsultation}
              className="bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-700 transition-colors duration-200 shadow-xl"
            >
              Request a Consultation
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752097997411-Namvoro-Light-logo.png"
              alt="Namvoro"
              className="h-8 w-auto mb-6"
            />
            <p className="text-slate-300 text-lg mb-6 max-w-md">
              Your silent partner in digital brand control. Enterprise-grade security, confidential service delivery, and unmatched reliability.
            </p>
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="font-semibold">Enterprise Security</div>
                <div className="text-slate-400 text-sm">Trusted by Fortune 500</div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">Domain Management</a></li>
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">DNS Solutions</a></li>
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">Email Security</a></li>
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">Cybersecurity</a></li>
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">Brand Protection</a></li>
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">Infrastructure Audits</a></li>
              <li><a href="#" className="text-slate-300 hover:text-emerald-400 transition-colors">Strategic Consulting</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-emerald-400 mr-3" />
                <span className="text-slate-300">contact@namvoro.com</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-emerald-400 mr-3" />
                <span className="text-slate-300">+1 855-425-5551</span>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-emerald-400 mr-3 mt-0.5" />
                <span className="text-slate-300">
                  200 Broadway, Floor 3<br />
                  New York, New York 10038
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-slate-400 mb-4 md:mb-0">
              © 2024 Clark-Ritchotte Communications and affiliated companies. All rights reserved. Enterprise digital brand management solutions.
            </div>
            <div className="flex space-x-6">
              <button
                onClick={() => navigateToPage('/privacy-policy')}
                className="text-slate-400 hover:text-emerald-400 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => navigateToPage('/terms-of-service')}
                className="text-slate-400 hover:text-emerald-400 transition-colors"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigateToPage('/security-architecture')}
                className="text-slate-400 hover:text-emerald-400 transition-colors"
              >
                Security
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;